import React, { useEffect, useMemo, useState } from 'react';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { isCompanyAdminUser } from "@shared/utils/AccessControl";
import AccessRequired from "@features/settings/pages/components/AccessRequired";
import { SpaceBetweenRow } from '@features/settings/pages/StyleComponents';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import moment, { Moment } from "moment";
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular, BodyLarge } from '@shared/ui/atoms/fonts/Body';
import { AutomaticCheckinLog, getAutomaticCheckins } from '@features/reporting/services/ReportingService';
import { failureNotification } from "@shared/store/notification.duck";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import Colours from '@shared/ui/atoms/Colours';
import { PRETTY_DATE_FORMAT } from '@shared/utils/DateUtils';

export function AutomaticOfficeCheckInReportingPage() {
  const [selectedDate, setSelectedDate] = useState<Moment>(moment());
  const [checkins, setCheckins] = useState<AutomaticCheckinLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasEverHadCheckins, setHasEverHadCheckins] = useState<boolean | null>(null);
  const currentUser = useSelector(selectCurrentUser);
  const canViewReport = useMemo(() => isCompanyAdminUser(currentUser), [currentUser]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (canViewReport) {
      setLoading(true);
      getAutomaticCheckins(selectedDate)
        .then(data => {
          setCheckins(data);
          if (data.length > 0) {
            setHasEverHadCheckins(true);
          } else if (hasEverHadCheckins === null) {
            setHasEverHadCheckins(false);
          }
        })
        .catch(() => dispatch(failureNotification("Failed to get automatic check-in data")))
        .finally(() => setLoading(false));
    }
  }, [canViewReport, selectedDate, dispatch]);

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting"} />
      {loading && <LoadingSpinner fullScreen={true} />}
      <div>
        {canViewReport ? <>
          <HeadlineLarge style={{marginBottom: 24}}>Automatic Office Check-ins</HeadlineLarge>

          {hasEverHadCheckins === false && !loading && (
            <SetupBanner>
              <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 8}}>No check-ins have been recorded yet</BodyLarge>
              <BodyRegular>It looks like automatic office check-in hasn't sent any data yet. Please ensure your network detection is configured and sending requests. If you need help, contact <a href="mailto:support@team-today.com">support@team-today.com</a>.</BodyRegular>
            </SetupBanner>
          )}

          <SpaceBetweenRow>
            <DateFlicker dateScale={DateScale.Day} updateGlobalDate={false} onChange={date => setSelectedDate(date)} date={selectedDate} disabled={loading} dateFormat={PRETTY_DATE_FORMAT} />
          </SpaceBetweenRow>

          {checkins.length === 0 && !loading && (
            <BodyRegular colour={Colours.darkGrey} style={{marginTop: 24}}>No check-ins found for this date.</BodyRegular>
          )}

          {checkins.length > 0 && (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>User ID</Th>
                    <Th>Network</Th>
                    <Th>MAC Address</Th>
                    <Th>Time</Th>
                  </tr>
                </thead>
                <tbody>
                  {checkins.map(checkin => (
                    <tr key={checkin.id}>
                      <Td>{checkin.userId}</Td>
                      <Td>{checkin.networkName || '-'}</Td>
                      <Td>{checkin.macAddress || '-'}</Td>
                      <Td>{moment(checkin.dateTime).format('HH:mm:ss')}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </> : <>
          <AccessRequired visible={!canViewReport} companyAdminsRequired={true} />
        </>}
      </div>
    </ReportingContainer>
  );
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 24px;
`

const SetupBanner = styled.div`
  background-color: ${Colours.blue10};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  a {
    color: ${Colours.blue};
    font-weight: 600;
  }
`

const TableWrapper = styled.div`
  margin-top: 24px;
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const Th = styled.th`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${Colours.mildGrey};
  white-space: nowrap;
`

const Td = styled.td`
  font-family: "soleil", sans-serif;
  font-size: 14px;
  color: ${Colours.black};
  padding: 10px 12px;
  border-bottom: 1px solid ${Colours.mildGrey};
`
